// Admin console — v2 design DNA. Mirrors the production /admin page
// surface (system health, stats, activity feed, curator mgmt, errors,
// failure trends, phase timings, audit log, dangerous reset actions)
// but rendered in the prototype's light hero + card grammar.
//
// Auto-refreshes system health every 5s, the rest is on-demand /
// react-query stale-while-revalidate.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { adminApi } from "@/lib/api/admin";
import type { CuratorRow, SystemHealth } from "@/lib/api/admin";
import { useUiStore } from "@/lib/store/uiStore";

type TabId =
  | "overview"
  | "infra"
  | "activity"
  | "errors"
  | "curators"
  | "performance"
  | "danger";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "infra", label: "Infrastructure" },
  { id: "activity", label: "Activity" },
  { id: "errors", label: "Errors" },
  { id: "curators", label: "Curators" },
  { id: "performance", label: "Performance" },
  { id: "danger", label: "Danger zone" },
];

export function Admin(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>("overview");

  useEffect(() => {
    document.title = "Admin · Generator";
  }, []);

  if (auth.loading) {
    return <div style={{ padding: 48, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>;
  }
  if (!auth.isAdmin) {
    return (
      <div style={{ padding: 48, maxWidth: 540, margin: "60px auto", textAlign: "center" }}>
        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Admin access required</h2>
          <p style={{ color: "var(--ink-2)", marginTop: 10, fontSize: 13 }}>
            Sign in with a Q Branch admin account or connect a Salesforce org tied to an admin user.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
            <button className="btn primary" onClick={() => navigate("/team-login")}>
              Q Branch sign in
            </button>
            <button className="btn ghost" onClick={() => auth.connect()}>
              Connect org
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminHero />
      <section style={{ background: "white", padding: "0 32px", borderBottom: "1px solid var(--bd)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", gap: 4 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                background: "transparent",
                border: 0,
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: tab === t.id ? 600 : 500,
                color: tab === t.id ? "var(--eb-50)" : "var(--ink-2)",
                borderBottom: tab === t.id ? "2px solid var(--eb-50)" : "2px solid transparent",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section style={{ padding: "28px 32px", background: "var(--cb-98)", minHeight: "60vh" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          {tab === "overview" && <OverviewTab />}
          {tab === "infra" && <InfraTab />}
          {tab === "activity" && <ActivityTab />}
          {tab === "errors" && <ErrorsTab />}
          {tab === "curators" && <CuratorsTab />}
          {tab === "performance" && <PerformanceTab />}
          {tab === "danger" && <DangerTab />}
        </div>
      </section>
    </div>
  );
}

function AdminHero(): JSX.Element {
  const navigate = useNavigate();
  return (
    <section className="hero-gradient" style={{ padding: "32px 32px 24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="label" style={{ marginBottom: 6 }}>Admin console</div>
          <h1 style={{ margin: 0, fontSize: 28, color: "var(--eb-15)", fontWeight: 700 }}>
            System operations
          </h1>
          <p style={{ color: "var(--ink-2)", marginTop: 6, fontSize: 13, maxWidth: 640 }}>
            Live health, audit trail, curator management, and dangerous resets.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn ghost" onClick={() => navigate("/admin/workshop")}>
            Workshop →
          </button>
          <button className="btn ghost" onClick={() => navigate("/curator")}>
            Curator queue →
          </button>
        </div>
      </div>
    </section>
  );
}

// ───────────────── Overview ─────────────────

function OverviewTab(): JSX.Element {
  const { data: health } = useQuery({
    queryKey: ["admin", "health"],
    queryFn: adminApi.systemHealth,
    refetchInterval: 5000,
  });
  const { data: stats } = useQuery({ queryKey: ["admin", "stats"], queryFn: adminApi.stats });
  const { data: pkg } = useQuery({ queryKey: ["admin", "pkg"], queryFn: adminApi.packageInfo });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Stat
          label="Total runs"
          value={stats?.totals?.total ?? "—"}
          sub={`${stats?.totals?.completed ?? 0} completed · ${stats?.totals?.failed ?? 0} failed`}
        />
        <Stat
          label="Success rate"
          value={
            stats?.totals?.success_rate_pct != null ? `${stats.totals.success_rate_pct.toFixed(0)}%` : "—"
          }
          sub={`avg ${formatSeconds(stats?.totals?.avg_duration_seconds)}`}
          accent="var(--teal)"
        />
        <Stat
          label="24h totals"
          value={
            health?.totals_24h
              ? `${(health.totals_24h.completed ?? 0) + (health.totals_24h.failed ?? 0)} jobs`
              : "—"
          }
          sub={`${health?.totals_24h?.failed ?? 0} failed · ${health?.totals_24h?.cancelled ?? 0} cancelled`}
        />
        <Stat
          label="Package"
          value={pkg?.version ?? "—"}
          sub={`${pkg?.installed_packs ?? 0} packs installed`}
          accent="var(--violet)"
        />
      </div>

      <SystemHealthCard health={health ?? null} />
      <ActiveJobsCard health={health ?? null} />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  accent = "var(--eb-50)",
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="label" style={{ marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SystemHealthCard({ health }: { health: SystemHealth | null }): JSX.Element {
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <div>
          <div className="label">System health</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Worker dynos & queues</div>
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
          Refresh every {((health?.refresh_interval_ms ?? 5000) / 1000).toFixed(0)}s · pressure{" "}
          <strong style={{ color: "var(--ink)" }}>{health?.queue_pressure ?? "—"}</strong>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        {(health?.workers ?? []).map((w) => (
          <div
            key={w.dyno}
            style={{
              border: "1px solid var(--bd)",
              borderRadius: 8,
              padding: 12,
              background: "var(--cb-98)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600 }}>{w.dyno}</div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
              mem {w.mem_pct?.toFixed(0) ?? "—"}% ({w.mem_mb?.toFixed(0) ?? "—"} MB) · cpu{" "}
              {w.cpu_pct?.toFixed(0) ?? "—"}%
            </div>
          </div>
        ))}
        {(health?.workers ?? []).length === 0 && (
          <div style={{ color: "var(--ink-3)", fontSize: 12 }}>No worker heartbeats yet.</div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 14 }}>
        <MiniMetric
          label="High queue"
          value={health?.queues?.find((q) => q.name === "high")?.depth ?? 0}
        />
        <MiniMetric
          label="Default queue"
          value={health?.queues?.find((q) => q.name === "default")?.depth ?? 0}
        />
        <MiniMetric
          label="DB pool"
          value={
            health?.db_pool
              ? `${health.db_pool.used ?? "?"}/${health.db_pool.size ?? "?"}`
              : "—"
          }
          sub={
            health?.db_pool?.saturation_pct != null
              ? `${health.db_pool.saturation_pct.toFixed(0)}% saturated`
              : undefined
          }
        />
        <MiniMetric
          label="AI throttle"
          value={`${health?.ai_throttle?.remaining ?? "—"}/${health?.ai_throttle?.limit ?? "—"}`}
          sub={health?.ai_throttle?.state}
        />
      </div>
    </div>
  );
}

function MiniMetric({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }): JSX.Element {
  return (
    <div style={{ borderLeft: "3px solid var(--eb-50)", padding: "6px 12px" }}>
      <div className="label" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ActiveJobsCard({ health }: { health: SystemHealth | null }): JSX.Element {
  const queryClient = useQueryClient();
  const showToast = useUiStore((s) => s.showToast);
  const cancel = useMutation({
    mutationFn: (id: string) => adminApi.forceCancel(id),
    onSuccess: () => {
      showToast("Job force-cancelled");
      void queryClient.invalidateQueries({ queryKey: ["admin", "health"] });
    },
    onError: (e) => showToast(`Cancel failed: ${(e as Error).message}`),
  });

  const jobs = health?.active_jobs ?? [];
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <div className="label">Active jobs</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Force-cancel a stuck run</div>
        </div>
        <div className="chip ghost">{jobs.length} running</div>
      </div>
      {jobs.length === 0 ? (
        <div style={{ color: "var(--ink-3)", fontSize: 12, padding: "16px 0" }}>No jobs running.</div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {jobs.map((j) => (
            <div
              key={j.job_id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 10,
                alignItems: "center",
                padding: "8px 10px",
                background: "var(--cb-98)",
                borderRadius: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{j.pack ?? j.job_id}</div>
                <div style={{ fontSize: 10, color: "var(--ink-3)" }}>{j.job_id}</div>
              </div>
              <div className="progress-track" style={{ width: 140 }}>
                <div className="progress-fill" style={{ width: `${j.pct ?? 0}%` }} />
              </div>
              <button
                className="btn ghost sm"
                onClick={() => {
                  if (confirm(`Force-cancel ${j.job_id}?`)) cancel.mutate(j.job_id);
                }}
              >
                Force cancel
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────── Infrastructure ─────────────────
// Dedicated real-time view of the Heroku stack: web + worker dynos,
// Redis queues, Postgres pool, AI throttle, and currently-running jobs.
// Refreshes every 3 seconds while open.

function InfraTab(): JSX.Element {
  const { data: health, dataUpdatedAt } = useQuery({
    queryKey: ["admin", "health", "infra"],
    queryFn: adminApi.systemHealth,
    refetchInterval: 3000,
  });
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "—";

  const workers = health?.workers ?? [];
  const queues = health?.queues ?? [];
  const queueDepth = queues.reduce((acc, q) => acc + (q.depth ?? 0), 0);
  const memAvg =
    workers.length > 0
      ? workers.reduce((acc, w) => acc + (w.mem_pct ?? 0), 0) / workers.length
      : 0;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div className="label">Live infrastructure</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            Heroku dynos &amp; queues
          </div>
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
          Auto-refresh every 3s · last updated {lastUpdated}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
        }}
      >
        <Stat
          label="Worker dynos"
          value={workers.length}
          sub={`${workers.filter((w) => (w.mem_pct ?? 0) > 80).length} hot`}
          accent={workers.length === 0 ? "var(--pink)" : "var(--teal)"}
        />
        <Stat
          label="Avg worker memory"
          value={`${memAvg.toFixed(0)}%`}
          accent={memAvg > 85 ? "var(--pink)" : "var(--ink)"}
        />
        <Stat
          label="Queue depth"
          value={queueDepth}
          sub={`pressure ${health?.queue_pressure ?? "—"}`}
          accent={queueDepth > 10 ? "var(--pink)" : "var(--ink)"}
        />
        <Stat
          label="Redis"
          value={health?.redis?.connected ? "Online" : "Offline"}
          sub={
            health?.redis?.clients != null
              ? `${health.redis.clients} clients`
              : undefined
          }
          accent={health?.redis?.connected ? "var(--teal)" : "var(--pink)"}
        />
      </div>

      <SystemHealthCard health={health ?? null} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 16,
        }}
      >
        <div className="card" style={{ padding: 18 }}>
          <div className="label" style={{ marginBottom: 10 }}>
            Worker processes
          </div>
          {workers.length === 0 && <Empty>No worker heartbeats yet.</Empty>}
          <div style={{ display: "grid", gap: 6 }}>
            {workers.map((w) => {
              const memPct = w.mem_pct ?? 0;
              const cpuPct = w.cpu_pct ?? 0;
              return (
                <div
                  key={w.dyno}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr 1fr 80px",
                    gap: 12,
                    padding: "10px 12px",
                    background: "var(--cb-98)",
                    borderRadius: 8,
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{w.dyno}</div>
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--ink-3)",
                        marginBottom: 3,
                      }}
                    >
                      Memory · {memPct.toFixed(0)}%{" "}
                      {w.mem_mb != null ? `(${w.mem_mb.toFixed(0)} MB)` : ""}
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(100, memPct)}%`,
                          background:
                            memPct > 85
                              ? "var(--pink)"
                              : memPct > 60
                                ? "#e0a341"
                                : "var(--teal)",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--ink-3)",
                        marginBottom: 3,
                      }}
                    >
                      CPU · {cpuPct.toFixed(0)}%
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(100, cpuPct)}%`,
                          background:
                            cpuPct > 85
                              ? "var(--pink)"
                              : cpuPct > 60
                                ? "#e0a341"
                                : "var(--eb-50)",
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className="chip ghost"
                    style={{ fontSize: 10, justifySelf: "end" }}
                  >
                    online
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <div className="label" style={{ marginBottom: 10 }}>
            Queues &amp; data plane
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {queues.map((q) => (
              <div
                key={q.name}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 10px",
                  background: "var(--cb-98)",
                  borderRadius: 8,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600 }}>{q.name}</div>
                <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
                  {q.depth ?? 0} queued
                </div>
              </div>
            ))}
            {queues.length === 0 && (
              <Empty>Redis returned no queue snapshots.</Empty>
            )}
          </div>
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background: "var(--cb-98)",
              borderRadius: 8,
            }}
          >
            <div className="label" style={{ marginBottom: 6 }}>
              Postgres pool
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {health?.db_pool
                ? `${health.db_pool.used ?? 0}/${health.db_pool.size ?? "?"} connections`
                : "—"}
            </div>
            {health?.db_pool?.saturation_pct != null && (
              <div className="progress-track" style={{ marginTop: 6 }}>
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.min(100, health.db_pool.saturation_pct)}%`,
                    background:
                      health.db_pool.saturation_pct > 80
                        ? "var(--pink)"
                        : "var(--teal)",
                  }}
                />
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: 10,
              padding: 12,
              background: "var(--cb-98)",
              borderRadius: 8,
            }}
          >
            <div className="label" style={{ marginBottom: 6 }}>
              AI throttle
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {health?.ai_throttle?.remaining ?? "—"} /{" "}
              {health?.ai_throttle?.limit ?? "—"}{" "}
              <span
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  fontWeight: 400,
                  marginLeft: 6,
                }}
              >
                {health?.ai_throttle?.state ?? ""}
              </span>
            </div>
          </div>
        </div>
      </div>

      <ActiveJobsCard health={health ?? null} />
    </div>
  );
}

// ───────────────── Activity ─────────────────

function ActivityTab(): JSX.Element {
  const { data: feed } = useQuery({ queryKey: ["admin", "feed"], queryFn: adminApi.activityFeed });
  const { data: audit } = useQuery({ queryKey: ["admin", "audit"], queryFn: () => adminApi.auditLog(50) });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Recent jobs (all users)</div>
        {(feed ?? []).length === 0 && <Empty>No recent activity.</Empty>}
        <div style={{ display: "grid", gap: 6 }}>
          {(feed ?? []).map((row) => (
            <div
              key={row.job_id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                padding: "8px 10px",
                borderBottom: "1px solid var(--bd-2)",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  {row.pack ?? row.job_id} · <span style={{ color: "var(--ink-2)", fontWeight: 400 }}>{row.user_email}</span>
                </div>
                <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>
                  {row.volume} · {row.output_mode} · {row.duration ? formatSeconds(Number(row.duration)) : "—"}
                </div>
              </div>
              <StatusChip status={row.status} />
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 4 }}>Audit log</div>
        <div
          style={{
            fontSize: 11,
            color: "var(--ink-3)",
            marginBottom: 10,
            lineHeight: 1.5,
          }}
        >
          Every privileged action — pack approve / reject / promote, run
          cancel, user role change, settings edit — is recorded with actor,
          target, and timestamp so admins can trace who changed what.
        </div>
        {(audit ?? []).length === 0 && <Empty>No audit entries.</Empty>}
        <div style={{ display: "grid", gap: 6, maxHeight: 520, overflowY: "auto" }} className="thin-scroll">
          {(audit ?? []).map((row) => (
            <div key={row.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--bd-2)" }}>
              <div style={{ fontSize: 11, fontWeight: 600 }}>
                {row.action} <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>by {row.actor}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--ink-3)" }}>
                {row.target ? `${row.target} · ` : ""}
                {row.created_at ?? ""}
              </div>
              {row.detail && (
                <div style={{ fontSize: 10, color: "var(--ink-2)", marginTop: 2 }}>{row.detail}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───────────────── Errors ─────────────────

function ErrorsTab(): JSX.Element {
  const { data: errors } = useQuery({ queryKey: ["admin", "errors"], queryFn: adminApi.errors });
  const { data: trends } = useQuery({
    queryKey: ["admin", "trends"],
    queryFn: () => adminApi.failureTrends(7),
  });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Last 25 failures</div>
        {(errors ?? []).length === 0 && <Empty>No recent errors.</Empty>}
        <div style={{ display: "grid", gap: 8 }}>
          {(errors ?? []).map((e) => (
            <div key={e.job_id} style={{ padding: 10, background: "#fdf3f4", borderRadius: 8, border: "1px solid #f3d3d6" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--pink)" }}>
                {e.pack ?? e.job_id} · <span style={{ color: "var(--ink-2)", fontWeight: 400 }}>{e.user_email}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{e.failed_at}</div>
              <pre style={{
                margin: "6px 0 0",
                fontSize: 11,
                color: "var(--ink-2)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
              }}>
                {e.error_message?.slice(0, 480) ?? "(no message)"}
              </pre>
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Failure categories (7 days)</div>
        {!trends?.by_category?.length && <Empty>No failure data.</Empty>}
        <div style={{ display: "grid", gap: 8 }}>
          {(trends?.by_category ?? []).map((c) => {
            const total = trends?.total ?? 0;
            const pct = total ? Math.round((c.count / total) * 100) : 0;
            return (
              <div key={c.category}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                  <span style={{ fontWeight: 600 }}>{c.category}</span>
                  <span style={{ color: "var(--ink-3)" }}>{c.count} · {pct}%</span>
                </div>
                <div className="progress-track" style={{ marginTop: 4 }}>
                  <div className="progress-fill" style={{ width: `${pct}%`, background: "var(--pink)" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ───────────────── Curators ─────────────────

function CuratorsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const showToast = useUiStore((s) => s.showToast);
  const { data: curators = [] } = useQuery({
    queryKey: ["admin", "curators"],
    queryFn: adminApi.curators,
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"curator" | "admin">("curator");

  const add = useMutation({
    mutationFn: () => adminApi.addCurator({ email: email.trim(), name: name.trim(), role }),
    onSuccess: () => {
      setEmail("");
      setName("");
      showToast(`${role} added`);
      void queryClient.invalidateQueries({ queryKey: ["admin", "curators"] });
    },
    onError: (e) => showToast(`Add failed: ${(e as Error).message}`),
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.removeCurator(id),
    onSuccess: () => {
      showToast("Removed");
      void queryClient.invalidateQueries({ queryKey: ["admin", "curators"] });
    },
  });
  const updateRole = useMutation({
    mutationFn: ({ id, r }: { id: string; r: "curator" | "admin" }) =>
      adminApi.updateCuratorRole(id, r),
    onSuccess: () => {
      showToast("Role updated");
      void queryClient.invalidateQueries({ queryKey: ["admin", "curators"] });
    },
  });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Add curator or admin</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            type="email"
            placeholder="email@salesforce.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="display name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as "curator" | "admin")} style={inputStyle}>
            <option value="curator">Curator</option>
            <option value="admin">Admin</option>
          </select>
          <button
            className="btn primary"
            onClick={() => add.mutate()}
            disabled={!email.trim() || add.isPending}
          >
            Add
          </button>
        </div>
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 10 }}>Team ({curators?.length ?? 0})</div>
        <div style={{ display: "grid", gap: 6 }}>
          {(curators ?? []).map((c: CuratorRow) => (
            <div
              key={c.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 12,
                alignItems: "center",
                padding: "10px 12px",
                background: "var(--cb-98)",
                borderRadius: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name || c.email}</div>
                <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{c.email}</div>
              </div>
              <select
                value={c.role}
                onChange={(e) => updateRole.mutate({ id: c.id, r: e.target.value as "curator" | "admin" })}
                style={{ ...inputStyle, padding: "4px 8px", fontSize: 11 }}
              >
                <option value="curator">Curator</option>
                <option value="admin">Admin</option>
              </select>
              <button
                className="btn ghost sm"
                onClick={() => {
                  if (confirm(`Remove ${c.email}?`)) remove.mutate(c.id);
                }}
              >
                Remove
              </button>
            </div>
          ))}
          {(curators ?? []).length === 0 && <Empty>No curators yet.</Empty>}
        </div>
      </div>
    </div>
  );
}

// ───────────────── Performance ─────────────────

function PerformanceTab(): JSX.Element {
  const { data: timings } = useQuery({
    queryKey: ["admin", "timings"],
    queryFn: () => adminApi.phaseTimings(14),
  });
  const phases = timings?.phases ?? [];
  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="label" style={{ marginBottom: 10 }}>Phase timings (14d window)</div>
      {phases.length === 0 && <Empty>No timing data yet.</Empty>}
      <div style={{ display: "grid", gap: 6 }}>
        {phases.map((p) => (
          <div
            key={p.step}
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 80px 80px 80px 80px",
              gap: 10,
              padding: "8px 12px",
              background: p.regressed ? "#fff8e7" : "var(--cb-98)",
              borderRadius: 8,
              border: p.regressed ? "1px solid #f0d28a" : "1px solid transparent",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {p.step}
              {p.regressed && (
                <span className="chip soft-yellow" style={{ marginLeft: 8, fontSize: 9 }}>
                  ↑ {p.delta_pct?.toFixed(0)}%
                </span>
              )}
            </div>
            <Cell label="median" value={formatSeconds(p.median_seconds)} />
            <Cell label="p95" value={formatSeconds(p.p95_seconds)} />
            <Cell label="recent" value={formatSeconds(p.recent_median_seconds)} />
            <Cell
              label="Δ"
              value={p.delta_pct != null ? `${p.delta_pct > 0 ? "+" : ""}${p.delta_pct.toFixed(0)}%` : "—"}
              accent={p.regressed ? "#8a5200" : "var(--ink-2)"}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({ label, value, accent = "var(--ink)" }: { label: string; value: React.ReactNode; accent?: string }): JSX.Element {
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 9, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: accent }}>{value}</div>
    </div>
  );
}

// ───────────────── Danger ─────────────────

function DangerTab(): JSX.Element {
  const showToast = useUiStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const purge = useMutation({
    mutationFn: () => adminApi.purgeQueues(),
    onSuccess: () => {
      showToast("Queues purged");
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (e) => showToast(`Purge failed: ${(e as Error).message}`),
  });
  const resetJobs = useMutation({
    mutationFn: (confirm: boolean) =>
      adminApi.resetJobHistory({ confirm, keep_completed: false, older_than_days: null }),
    onSuccess: (data, confirmed) => {
      showToast(
        confirmed
          ? `Reset complete · ${data?.deleted ?? 0} rows`
          : `Dry run · ${data?.would_delete ?? 0} would delete`,
      );
    },
    onError: (e) => showToast(`Reset failed: ${(e as Error).message}`),
  });
  const resetPacks = useMutation({
    mutationFn: (confirm: boolean) => adminApi.resetDraftPacks({ confirm }),
    onSuccess: (data, confirmed) => {
      showToast(
        confirmed
          ? `Cleaned · ${data?.packs ?? 0} draft/rejected packs`
          : `Dry run · ${data?.would_delete ?? 0} would delete`,
      );
    },
  });

  return (
    <div className="card" style={{ padding: 18, borderColor: "#f3d3d6", background: "#fdf6f7" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div>
          <div className="label" style={{ color: "var(--pink)" }}>Danger zone</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Irreversible operations</div>
        </div>
      </div>
      <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
        <DangerRow
          title="Purge RQ queues"
          desc="Empty high + default queues. Cancel any queued or running jobs."
          actionLabel="Purge"
          onAction={() => {
            if (confirm("Purge all queues? Currently running jobs will be cancelled.")) purge.mutate();
          }}
          busy={purge.isPending}
        />
        <DangerRow
          title="Reset job history"
          desc="Delete all job + notification rows. Use the dry run first to preview."
          actionLabel="Dry run"
          secondaryLabel="Confirm reset"
          onAction={() => resetJobs.mutate(false)}
          onSecondary={() => {
            if (confirm("Permanently delete ALL job history? This cannot be undone.")) {
              resetJobs.mutate(true);
            }
          }}
          busy={resetJobs.isPending}
        />
        <DangerRow
          title="Reset draft / rejected packs"
          desc="Delete all packs in draft, rejected, or submitted state."
          actionLabel="Dry run"
          secondaryLabel="Confirm clean"
          onAction={() => resetPacks.mutate(false)}
          onSecondary={() => {
            if (confirm("Permanently delete all draft / rejected / submitted packs?")) {
              resetPacks.mutate(true);
            }
          }}
          busy={resetPacks.isPending}
        />
      </div>
    </div>
  );
}

function DangerRow({
  title,
  desc,
  actionLabel,
  secondaryLabel,
  onAction,
  onSecondary,
  busy,
}: {
  title: string;
  desc: string;
  actionLabel: string;
  secondaryLabel?: string;
  onAction: () => void;
  onSecondary?: () => void;
  busy?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 12,
        padding: 14,
        background: "white",
        border: "1px solid var(--bd)",
        borderRadius: 10,
        alignItems: "center",
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 2 }}>{desc}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn ghost sm" onClick={onAction} disabled={busy}>
          {actionLabel}
        </button>
        {onSecondary && (
          <button className="btn danger sm" onClick={onSecondary} disabled={busy}>
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ───────────────── Helpers ─────────────────

function StatusChip({ status }: { status?: string }): JSX.Element {
  const map: Record<string, { cls: string; label: string }> = {
    completed: { cls: "soft-teal", label: "Complete" },
    running: { cls: "soft-yellow", label: "Running" },
    queued: { cls: "ghost", label: "Queued" },
    failed: { cls: "soft-violet", label: "Failed" },
    cancelled: { cls: "ghost", label: "Cancelled" },
  };
  const m = map[status ?? ""] ?? { cls: "ghost", label: status ?? "?" };
  return <span className={`chip ${m.cls}`} style={{ fontSize: 10 }}>{m.label}</span>;
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ textAlign: "center", color: "var(--ink-3)", fontSize: 12, padding: "20px 0" }}>
      {children}
    </div>
  );
}

function formatSeconds(s?: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  return `${mins}m ${secs}s`;
}

const inputStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid var(--bd)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 12,
  color: "var(--ink)",
  outline: "none",
  flex: "0 0 auto",
};

// Avoid unused-import lint
useMemo;
